
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAssessment } from './AssessmentContext';
import { mapChangesToTests } from '../../services/apiService';
import * as XLSX from 'xlsx';

const VerificationReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assessmentTestCases, setAssessmentTestCases] = useState(false);
  const [testCasesData, setTestCasesData] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { getCurrentTabData, currentTab} = useAssessment();
  const currentTabData = getCurrentTabData();
  
  const selectedChanges = currentTabData.selectedChanges || {};
  const activeTab = currentTab || 'TV';
  const referenceModels = currentTabData.referenceModels || {};
  const changeListId = currentTabData.changeListId;
  const changeListData = currentTabData.changeListData;

  const assessmentItems = ['EE', 'PWR', 'ME', 'SW', 'LCM'];
  const [forceUpdate, setForceUpdate] = useState(0);
  
  useEffect(() => {
    console.log('VerificationReview - Selected changes updated:', selectedChanges);
    setForceUpdate(prev => prev + 1);
  }, [selectedChanges]);

  const handleBackClick = () => {
    navigate('/hardware-change', {
      state: {
        activeTab,
        selectedChanges: selectedChanges,
        referenceModels: referenceModels,
        changeListId,
        changeListData
      }
    });
  };

  // Function to sort change IDs while preserving the original order of different prefixes
  const sortChangeIds = (changeIds) => {
    // Group by prefix
    const grouped = {};
    const order = []; // To maintain original order of prefixes
    
    changeIds.forEach(id => {
      const match = id.match(/^([A-Z]+)(\d+(?:\.\d+)?)$/);
      if (match) {
        const [, prefix, number] = match;
        if (!grouped[prefix]) {
          grouped[prefix] = [];
          order.push(prefix); // Track first occurrence order
        }
        grouped[prefix].push({ id, number: parseFloat(number) });
      }
    });
    
    // Sort within each group and reconstruct
    const result = [];
    order.forEach(prefix => {
      const sorted = grouped[prefix].sort((a, b) => a.number - b.number);
      result.push(...sorted.map(item => item.id));
    });
    
    return result;
  };

  // Function to merge duplicate test cases and combine change IDs
  const mergeDuplicateTestCases = (testCases) => {
    const mergedMap = new Map();
    
    testCases.forEach(testCase => {
      // Create a unique key based on Test Category and Test Item
      const key = `${testCase.testCategory}|||${testCase.testItem}`;
      
      if (mergedMap.has(key)) {
        // If duplicate exists, merge the change IDs
        const existing = mergedMap.get(key);
        
        // Combine change IDs (remove duplicates while preserving order)
        const combinedChangeIds = [...existing.changeIds];
        testCase.changeIds.forEach(id => {
          if (!combinedChangeIds.includes(id)) {
            combinedChangeIds.push(id);
          }
        });
        
        // Update the merged entry with sorted change IDs
        mergedMap.set(key, {
          ...existing,
          changeIds: sortChangeIds(combinedChangeIds),
        });
      } else {
        // First occurrence, add to map with sorted change IDs
        mergedMap.set(key, { 
          ...testCase,
          changeIds: sortChangeIds([...testCase.changeIds])
        });
      }
    });
    
    // Convert map back to array and re-number
    const mergedArray = Array.from(mergedMap.values());
    return mergedArray.map((item, index) => ({
      ...item,
      no: index + 1
    }));
  };

  const handleAssessmentTestCases = async () => {
    try {
      setLoadingTestCases(true);
      setAssessmentTestCases(true);
      setErrorMessage(''); // Clear previous error
      
      // Map selected changes to test cases using template
      const response = await mapChangesToTests(activeTab, selectedChanges);
      
      if (response.success) {
        // Merge duplicate test cases
        const mergedTestCases = mergeDuplicateTestCases(response.data);
        setTestCasesData(mergedTestCases);
      } else {
        setTestCasesData([]);
        setErrorMessage('');      }
    } catch (error) {
      setTestCasesData([]);
    } finally {
      setLoadingTestCases(false);
    }
  };

  const handleExportToExcel = () => {
    if (testCasesData.length === 0) {
      alert('No test cases to export');
      return;
    }

    const workbook = XLSX.utils.book_new();
    
    const worksheetData = [
      ['No.', 'Test Category', 'Test Item', 'Corresponding Change Item ID', 'Q\'ty'],

      ...testCasesData.map(row => [
        row.no, 
        row.testCategory, 
        row.testItem, 
        row.changeIds.join('; '),
        row.qty
      ])
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "CCCCCC" } }
        };
      }
    }
    
    worksheet['!cols'] = [
      { width: 5 },
      { width: 25 },
      { width: 30 },
      { width: 25 },
      { width: 8 }
    ];
    
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assessment Test Cases');
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g,'-');
    const fileName = `Assessment_Test_Cases_${activeTab}_${dateStr}_${timeStr}.xlsx`;

    XLSX.writeFile(workbook, fileName);

  }; 

  const handleBackFromTestCases = () => {
    setAssessmentTestCases(false);
  };

  if (assessmentTestCases) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button
                onClick={handleBackFromTestCases}
                className="flex items-center justify-center w-12 h-12 -ml-2 mr-3 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors duration-200 touch-manipulation"
                aria-label="Go back"
              >
                <ArrowLeft className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
              </button>
              <h1 className="text-2xl font-semibold text-white">Assessment Test Cases - {activeTab}</h1>
            </div>
            
            <button
              onClick={handleExportToExcel}
              disabled={testCasesData.length === 0}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-800 rounded-lg text-white font-medium transition-all duration-200 text-base min-h-[48px] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-5 h-5" />
              Export to Excel
            </button>
          </div>
        </div>

        <div className="px-6 py-8 max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-gray-400 text-base">
              Recommended Verification Test Items for {activeTab} 
              {testCasesData.length > 0 && ` (${testCasesData.length} test cases, ${testCasesData.reduce((sum, t) => sum + t.qty, 0)} total tests)`}
            </p>
          </div>


          {loadingTestCases ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading test cases from template...</p>
              </div>
            </div>
          ) : testCasesData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-gray-500 text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-400">No Test Cases Found</h3>
              <p className="text-gray-500 text-center max-w-md">
                No test cases were generated. This may happen if:
              </p>
              <ul className="text-gray-500 text-sm mt-2 space-y-2">
                <li>• No test cases present for the selected content Detail </li>
                <li>• The template uploaded by the admin for {activeTab} does not contain any test cases for selected content Detail </li>
              </ul>
            </div>
          ) : (
            <>
              <div className="bg-gray-900/40 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
                <div className="grid grid-cols-5 bg-gradient-to-r from-gray-800/80 to-gray-700/80 border-b border-gray-600">
                  <div className="px-8 py-6 text-base font-semibold text-gray-100 border-r border-gray-600 text-center">No.</div>
                  <div className="px-8 py-6 text-base font-semibold text-gray-100 border-r border-gray-600 text-center">Test Category</div>
                  <div className="px-8 py-6 text-base font-semibold text-gray-100 border-r border-gray-600 text-center">Test Item</div>
                  <div className="px-8 py-6 text-base font-semibold text-gray-100 border-r border-gray-600 text-center">Corresponding Change Item ID</div>
                  <div className="px-8 py-6 text-base font-semibold text-gray-100 text-center">Q'ty</div>
                </div>

                {testCasesData.map((row, index) => (
                  <div 
                    key={index}
                    className="grid grid-cols-5 hover:bg-gray-800/40 transition-all duration-300 border-b border-gray-700/50 last:border-b-0 group text-center"
                  >
                    <div className="px-8 py-6 text-base text-white font-medium border-r border-gray-700/50 group-hover:text-blue-400 transition-colors">
                      {row.no}
                    </div>
                    <div className="px-8 py-6 text-base text-gray-300 border-r border-gray-700/50 group-hover:text-gray-200 transition-colors">
                      {row.testCategory}
                    </div>
                    <div className="px-8 py-6 text-base text-white border-r border-gray-700/50 group-hover:text-gray-100 transition-colors">
                      {row.testItem}
                    </div>
                    <div className="px-8 py-6 text-sm text-gray-400 border-r border-gray-700/50 group-hover:text-gray-300 transition-colors">
                      {row.changeIds.join('; ')}
                    </div>
                    <div className="px-8 py-6 text-base text-white text-center font-semibold group-hover:text-blue-400 transition-colors">
                      {row.qty}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 bg-gray-900/30 rounded-xl p-8 border border-gray-700/50 space-y-6">
                <div className="flex items-start gap-4 text-base text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white text-lg min-w-[24px]">1.</span>
                  <span className="text-gray-300">
                    Test cases are automatically generated based on selected change items and the uploaded template for {activeTab}.
                  </span>
                </div>
                <div className="flex items-start gap-4 text-base text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white text-lg min-w-[24px]">2.</span>
                  <span className="text-gray-300">
                    Duplicate test items are automatically merged, and corresponding change IDs are combined with numbers sorted within each prefix group.
                  </span>
                </div>
                <div className="flex items-start gap-4 text-base text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white text-lg min-w-[24px]">3.</span>
                  <span className="text-gray-300">
                    Click "Export to Excel" to download the complete test case list with change item mappings.
                  </span>
                </div>
                <div className="flex items-start gap-4 text-base text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white text-lg min-w-[24px]">4.</span>
                  <span className="text-gray-300">
                    Test quantities are aggregated from all selected change items that require the same test.
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const hasAnySelectedChanges = assessmentItems.some(item => 
    selectedChanges[item] && selectedChanges[item].length > 0
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm ">
        <div className="px-4 py-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-1">
            <h1 className="pt-4 text-base font-medium sm:text-3xl text-white mb-3 sm:mb-0 text-center sm:text-left">
              Verification Cases Review - {activeTab}
            </h1>
            <div className="flex flex-col sm:items-end space-y-2">
              <button
                onClick={handleBackClick}
                
                className="px-6 sm:px-25  sm:py-1 bg-gray-600 hover:bg-gray-700 active:bg-gray-800 
             rounded-full text-white font-medium transition-all duration-200 
             text-sm sm:text-base min-h-[36px] sm:min-h-[40px] shadow-lg hover:shadow-xl"
              >
                Back
              </button>

              <button
                onClick={handleAssessmentTestCases}
               
                className="px-4 sm:px-9  sm:py-1 bg-blue-600 hover:bg-blue-700 active:bg-gray-800 
             rounded-full text-white font-medium transition-all duration-200 
             text-sm sm:text-base min-h-[36px] sm:min-h-[40px] shadow-lg hover:shadow-xl"
              >
                Assessment Test Cases
              </button>
            </div>
          </div>
        </div>

      </div>


            <div className="justify-left px-4 py-6 sm:px-6 max-w-6xl" key={forceUpdate}>
        <div className="flex justify-between items-start">
          {/* Left side — Table section */}
          <div className="flex-1">


        {assessmentItems.map((item) => {
          const changes = selectedChanges[item] || [];
          const appliedChanges = changes.filter(change => change.applied);
          
          if (appliedChanges.length === 0) return null;

          const referenceModel = referenceModels[item] || 'N/A';

          return (
            <div key={item} className="mb-8">
              <div className="bg-gray-800/30 rounded-t-lg px-4 py-3 border border-gray-700">
                <h2 className="text-lg font-semibold text-white">
                  {`${item} Change Evaluation Against Reference Model : ${referenceModel}`}
                </h2>
              </div>

              <div className="bg-gray-900/20 border-l border-r border-b border-gray-700 rounded-b-lg">
                <div className="grid grid-cols-2 bg-gray-800/50 border-b border-gray-700">
                  <div className="px-4 py-3 text-sm font-medium text-gray-300 border-r border-gray-700">
                    Change Item ID
                  </div>
                  <div className="px-4 py-3 text-sm font-medium text-gray-300">
                    Change Content Detail
                  </div>
                </div>

                {appliedChanges.map((change, changeIndex) => (
                  <div 
                    key={`${item}-${change.id}-${changeIndex}`} 
                    className="grid grid-cols-2 hover:bg-gray-800/30 transition-colors duration-200 border-b border-gray-700/50 last:border-b-0"
                  >
                    <div className="px-4 py-3 text-sm text-blue-400 font-medium border-r border-gray-700/50">
                      {change.id}
                    </div>
                    <div className="px-4 py-3 text-sm text-white leading-relaxed">
                      {change.detail}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        </div>
        </div>

        {!hasAnySelectedChanges && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-500 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold mb-2 text-gray-400">No Changes Selected</h3>
            <p className="text-gray-500 text-center max-w-md">
              No changes have been selected from any assessment sections. 
              Please go back and select changes to review.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationReview;